/**
 * @file Body of the restore confirmation dialog (SYD-2450 phase 2): a summary of
 * everything this restore would do, the one table of changes that backs it up, and
 * the Confirm/Cancel actions. Mounted only while `FormRecoveryConfirmModal` is open
 * (`destroyOnHidden`), so it reads `formRecoveryStore` and `formStore` directly
 * rather than taking the diff as a prop.
 *
 * One restore covers the form version AND the ArTables of the moment, because to the
 * operator the table is part of the form. So the summary counts both — values on the
 * form, and per table the rows that change, come back and go away — and the changes
 * below it are one list grouped by source rather than a table per source. A moment
 * naming no table renders exactly the three-column list this dialog always showed.
 *
 * A group states its own trouble rather than looking unchanged: a table still being
 * read, a table the server refused, and the entries past `AR_TABLE_PAGE_SIZE` that are
 * on the wire and part of the restore but not on the screen.
 *
 * A table that could not be read holds the restore, exactly as a table still being read
 * does. It has no digests, so `useFormRecoveryRestore` cannot name it in the one
 * transaction — and a restore that ran without it would leave the form at the retained
 * moment and that table at the present. The operator closes this and selects the moment
 * again: the read is what is transient, not the history.
 *
 * A locked row cannot be written. Those rows sort to the top of their group behind a
 * notice naming them, carry the reason, and are counted in the summary; the confirm
 * action then says in its own words that it proceeds without them, because a partial
 * restore the operator did not read as partial is the one outcome this dialog exists to
 * prevent. That choice is what travels to the callable as `skipBlockedRows` — this
 * dialog owns it, and the hook never assumes it.
 *
 * The locked block and the rows being written are paged against `AR_TABLE_PAGE_SIZE`
 * separately, and `Restoring covers all` counts the written rows alone. One list shared
 * between them spent the page on rows the restore skips and claimed a reach it did not
 * have — at a hundred locked rows, every row on the screen was one the restore leaves
 * and every row it writes was hidden.
 *
 * `useFormRecoveryRestore` is called here for the `restore` function itself;
 * its registration effect also runs for the life of this mount, which is
 * harmless (`registerTarget` is refcounted) and not what keeps the undo entry
 * alive after this dialog closes — `FormPageContent` mounts the same hook for
 * that.
 *
 * `failed-precondition` has three server causes and only one of them —
 * the live baseline moving — carries `RestoreFormVersionConflict` in
 * `details`; the other two (nothing retained at that moment, or an
 * unresolvable legacy grouped entity reference) carry none. `readRestoreConflict`
 * is read defensively for that reason: when it decodes, the writer and moment
 * are named; otherwise the server's own message is shown as-is, because it
 * already states the cause in plain words. Either way nothing was written, so
 * the Confirm button is disabled rather than left to retry the same refusal —
 * the operator must close this dialog and select the restore point again.
 *
 * The headline names the MOMENT — the same time the sidebar row named and the same
 * one the banner names afterwards, so all three agree. It cannot be the version's
 * commit time any longer: a moment at which only a table reported has no version
 * commit behind it, and falling through to the version's last save would ask the
 * operator to approve the 14:32 they clicked under a heading reading 09:15.
 *
 * The version's own commit time keeps its place one level down, as a line inside the
 * version's group — and THERE the read's answer still outranks the row's, for the
 * reason it always did. The two differ when a cursor crosses the exact-time margin
 * between the walk and the read: the address becomes a whole minute, Firestore may
 * serve a neighbouring version out of it, and the values in this table are that
 * version's. Naming it is how the group says which save its values came from. The line
 * is drawn only when that time differs from the moment, because on an ordinary version
 * moment the two are one fact stated twice.
 *
 * `restoring` comes off `formRecoveryStore` rather than off `useFormRecoveryRestore`,
 * so the sidebar row and this dialog show one in-flight write between them.
 *
 * A landed restore is itself an edit, and so a restore point of its own that the
 * append-only walk can never reach. `refresh` re-walks the head for it — the one
 * automatic refetch in this feature, paid on an action the operator took.
 */

import { FC, useContext, useState } from 'react';
import { Alert, Button, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import { useSnapshot } from 'valtio';
import type { Snapshot } from 'valtio';

import type {
  ArTableId,
  ArTableRowDiffEntry,
  ArTableType,
  FormPitrDiffEntry,
  FormPitrValue,
  RestoreFormVersionConflict,
} from '#types';
import { AR_TABLE_PAGE_SIZE, formVersionNumberPrefix } from '#types/System.Parameters.types';
import FlexBox from '../../atoms/FlexBox';
import FlexCol from '../../atoms/FlexCol';
import { FieldTypeContext } from '../../../providers/FieldTypeContext';
import { MessageContext } from '../../../providers/MessageContext';
import { useFormRecoveryList } from '../../../use/form/useFormRecoveryList';
import { useFormRecoveryRestore } from '../../../use/form/useFormRecoveryRestore';
import { readCallableError } from '../../../workflows/runtime/readCallableError';
import { formRecoveryStore, formStore, useStoreDocsByPaths } from '../../../store';
import FormRecoveryChangeTable from './FormRecoveryChangeTable';
import type {
  RecoveryChangeCell,
  RecoveryChangeEntry,
  RecoveryChangeGroup,
} from './FormRecoveryChangeTable';
import { formatPitrValue } from './formatPitrValue';

const { Text } = Typography;

/** A restore point's commit time, named in full: it may be days back. */
const MOMENT_FORMAT = 'MMM D, YYYY h:mm:ss A';

/**
 * The row keys a restore may write, in the words the table itself uses for them. A
 * bundle is a kit or a group on screen and has never been called anything else.
 */
const ROW_KEY_LABELS: Record<ArTableRowDiffEntry['row'][number]['key'], string> = {
  order: 'Order',
  annotation: 'Annotation',
  bundleId: 'Kit or group',
  bundleRole: 'Kit or group role',
  bundleType: 'Kit or group type',
  bundleValue: 'Kit or group value',
};

/**
 * The referenced record's name off a diff side, the same shape
 * `FormRecoveryGroup` reads — `FormPitrValue.value` is raw Firestore data, so
 * the one property this needs is checked, never asserted.
 */
const entityRecordNameOf = (side: FormPitrValue | null): string | undefined => {
  if (side === null || typeof side.value !== 'object' || side.value === null) return undefined;
  const name = (side.value as { entityRecordName?: unknown }).entityRecordName;
  return typeof name === 'string' ? name : undefined;
};

/**
 * Decodes `RestoreFormVersionConflict` off a `failed-precondition`'s `details`,
 * or `undefined` when the shape does not match — the other two causes carry no
 * `details` at all, and an absent or differently-shaped payload must fall
 * through to the server's own message rather than be misread as "changed by
 * undefined".
 */
const readRestoreConflict = (error: unknown): RestoreFormVersionConflict | undefined => {
  if (!(error instanceof Error) || !('details' in error)) return undefined;
  const details = (error as { details?: unknown }).details;
  if (typeof details !== 'object' || details === null) return undefined;
  const bag = details as Record<string, unknown>;
  const lastModifiedMillis = bag.lastModifiedMillis;
  const userId = bag.userId;
  const validLastModified = lastModifiedMillis === null || typeof lastModifiedMillis === 'number';
  const validUserId = userId === null || typeof userId === 'string';
  if (!validLastModified || !validUserId) return undefined;
  return { lastModifiedMillis: lastModifiedMillis as number | null, userId: userId as string | null };
};

const describeConflict = (conflict: RestoreFormVersionConflict): string => {
  const when =
    conflict.lastModifiedMillis === null
      ? 'a save with no recorded stamp'
      : dayjs(conflict.lastModifiedMillis).format(MOMENT_FORMAT);
  const who = conflict.userId === null ? 'someone' : `user ${conflict.userId}`;
  return `This version was saved by ${who} at ${when} since you read it. Nothing was written — close this and select the restore point again.`;
};

/**
 * A row entry as `useSnapshot` hands it over: deep-readonly, and so not an
 * `ArTableRowDiffEntry` as far as its arrays are concerned. Spelled once here rather
 * than recast at each call site — the readonly-ness is true, and dropping it would
 * claim these components may write into the store's read results.
 */
type RowDiffSnapshot = Snapshot<ArTableRowDiffEntry>;

/**
 * The row's own name: its label column's value, or its id when that column held
 * nothing at all — absent, cleared, an empty string or an empty list. `Empty` names no
 * row, and an id at least addresses one; it is also what keeps `Show the cells of …`
 * unique per row, which one word shared by every unnamed row would not.
 */
const rowNameOf = (entry: RowDiffSnapshot): string => {
  const value = entry.label?.value ?? null;
  const names =
    value !== null && value !== '' && !(Array.isArray(value) && value.length === 0);
  return names ? formatPitrValue(entry.label) : entry.rowId;
};

/** `2 rows come back`, or `1 row comes back` — the verb follows the count. */
const rowPhrase = (count: number, one: string, many: string): string =>
  `${count} ${count === 1 ? `row ${one}` : `rows ${many}`}`;

type FormRecoveryConfirmModalContentProps = {
  onClose: () => void;
};

const FormRecoveryConfirmModalContent: FC<FormRecoveryConfirmModalContentProps> = ({
  onClose,
}) => {
  const { fieldTypes } = useContext(FieldTypeContext);
  const { messageApi, handleFirestoreWriteError } = useContext(MessageContext);
  const formSnap = useSnapshot(formStore.state);
  const entityReferences = formSnap.formVersion.data?.entityReferences;
  const versionNumber = formSnap.formVersion.data?.formVersionNumber;
  const recoverySnap = useSnapshot(formRecoveryStore.state);
  const { restore } = useFormRecoveryRestore();
  const { refresh } = useFormRecoveryList();

  const [refusalMessage, setRefusalMessage] = useState<string | null>(null);

  const result = recoverySnap.result;
  const restoring = recoverySnap.restoring;
  const selectedCursorMicros = recoverySnap.selectedCursorMicros;
  const tableReads = recoverySnap.tableReads;
  const selectedMoment = recoverySnap.moments.find(
    (moment) => moment.cursorMicros === selectedCursorMicros,
  );

  /**
   * The moment the operator clicked, which every other surface names too. The read's
   * own commit time stands in only while the timeline is not loaded — a state this
   * dialog cannot be opened from, kept so a label is never missing outright.
   */
  const momentAtMillis = selectedMoment?.atMillis ?? result?.committedAtMillis ?? null;

  /**
   * Which save of the version these values came from. Worth stating only when it is
   * not the moment already in the headline: on a table-only moment the version last
   * moved earlier, and on a cursor past the exact-time margin the minute may have
   * served a neighbouring version.
   */
  const versionCommittedAtMillis =
    result?.committedAtMillis ?? selectedMoment?.version?.committedAtMillis ?? null;
  const versionSavedAt =
    versionCommittedAtMillis === null || versionCommittedAtMillis === momentAtMillis
      ? null
      : versionCommittedAtMillis;

  const diff = result?.outcome === 'version' ? result.diff : [];

  /**
   * The tables' own names. Subscribed by path, which the document registry dedupes
   * against the `ArTableShell` and the sidebar group already holding the same
   * `tableTypes/{tableId}` open, so this costs no second listener. A name not back yet
   * falls through to the id rather than to a coined noun.
   */
  const { data: tableTypes } = useStoreDocsByPaths<ArTableType>(
    tableReads.map((read) => `tableTypes/${read.tableId}`),
  );

  const versionName =
    versionNumber === undefined ? 'This form' : `${formVersionNumberPrefix}${versionNumber}`;

  const tableNameOf = (tableId: ArTableId): string =>
    tableTypes.get(`tableTypes/${tableId}`)?.description?.shortLabel ?? tableId;

  /**
   * The live name first, then the one the retained side carried: a reference
   * cleared since the restore point was read has no live entry at all, and the
   * response carries its name for exactly that case.
   */
  const nameDiffEntry = (entry: FormPitrDiffEntry): string =>
    entry.kind === 'field'
      ? fieldTypes[entry.fieldId]?.description?.shortLabel ?? entry.fieldId
      : entityReferences?.[entry.entityOrGroupId]?.entityRecordName ??
        entityRecordNameOf(entry.retained) ??
        entry.entityOrGroupId;

  /** Every restorable cell and row key of one row, named for the screen. */
  const cellsOf = (entry: RowDiffSnapshot): RecoveryChangeCell[] => [
    ...entry.cells.map((cell) => ({
      key: `cell:${cell.typeId}`,
      name: fieldTypes[cell.typeId]?.description?.shortLabel ?? cell.typeId,
      live: cell.live,
      retained: cell.retained,
    })),
    ...entry.row.map((key) => ({
      key: `key:${key.key}`,
      name: ROW_KEY_LABELS[key.key],
      live: key.live,
      retained: key.retained,
    })),
  ];

  /**
   * One table's contribution to both the summary and the list. The counts cover only
   * the rows this restore will actually write, so a locked row is counted once — as
   * locked — and never again as a row that changes.
   */
  const tables = tableReads.map((read) => {
    const name = tableNameOf(read.tableId);
    const rows = read.result?.outcome === 'version' ? read.result.diff : [];
    const locked = rows.filter((row) => row.liveLocked);
    const writable = rows.filter((row) => !row.liveLocked);
    const edited = writable.filter((row) => row.action === 'edit').length;
    const recreated = writable.filter((row) => row.action === 'recreate').length;
    const deleted = writable.filter((row) => row.action === 'delete').length;
    const phrases = [
      ...(edited > 0 ? [rowPhrase(edited, 'changes', 'change')] : []),
      ...(recreated > 0 ? [rowPhrase(recreated, 'comes back', 'come back')] : []),
      ...(deleted > 0 ? [rowPhrase(deleted, 'goes away', 'go away')] : []),
    ];

    /**
     * Each side is paged against the cap on its own, so a page of locked rows can
     * never spend the room the rows being written need: with a hundred locked rows a
     * single list showed the operator only rows the restore skips and hid every row it
     * writes. The locked block keeps the top — it is what the confirm action names, and
     * it is introduced by its own notice — and the overflow line beneath the writable
     * rows counts those rows alone, because that is all `Restoring covers` reaches.
     */
    const lockedShown = locked.slice(0, AR_TABLE_PAGE_SIZE);
    const lockedHidden = locked.length - lockedShown.length;
    const shown = writable.slice(0, AR_TABLE_PAGE_SIZE);
    const hidden = writable.length - shown.length;

    const rowEntry = (row: RowDiffSnapshot): RecoveryChangeEntry => ({
      kind: 'row',
      key: `${read.tableId}:${row.rowId}`,
      name: rowNameOf(row),
      action: row.action,
      locked: row.liveLocked,
      inTrash: row.inTrash,
      cells: cellsOf(row),
    });

    const lockedNotice =
      locked.length === 0
        ? null
        : `${name} holds ${
            locked.length === 1 ? '1 locked row' : `${locked.length} locked rows`
          }, which this restore does not write.${
            lockedHidden === 0
              ? ''
              : ` ${lockedHidden} of them ${lockedHidden === 1 ? 'is' : 'are'} not listed here.`
          }`;

    const trouble =
      read.status === 'reading'
        ? `${name} is still being read.`
        : read.status === 'refused'
          ? `${name} could not be read — ${read.refusal}. This moment cannot be restored without it.`
          : rows.length === 0
            ? `Nothing changes in ${name}.`
            : null;

    const entries: RecoveryChangeEntry[] = [
      ...(lockedNotice === null
        ? []
        : [{ kind: 'notice' as const, key: `${read.tableId}:locked`, text: lockedNotice }]),
      ...lockedShown.map(rowEntry),
      ...shown.map(rowEntry),
    ];

    const notices: RecoveryChangeEntry[] = [
      ...(trouble === null ? [] : [{ kind: 'notice' as const, key: `${read.tableId}:state`, text: trouble }]),
      ...(hidden === 0
        ? []
        : [
            {
              kind: 'notice' as const,
              key: `${read.tableId}:more`,
              text: `${hidden} more ${hidden === 1 ? 'row is' : 'rows are'} not listed here. Restoring covers all ${writable.length}.`,
            },
          ]),
    ];

    const summary =
      read.status === 'reading'
        ? `${name}: still being read.`
        : read.status === 'refused'
          ? `${name}: could not be read, so this moment cannot be restored.`
          : phrases.length === 0
            ? `${name}: nothing changes.`
            : `${name}: ${phrases.join(', ')}.`;

    return {
      lockedCount: locked.length,
      summary,
      group: { key: read.tableId, name, entries: [...entries, ...notices] },
    };
  });

  const lockedCount = tables.reduce((total, table) => total + table.lockedCount, 0);
  const grouped = tables.length > 0;

  const shownDiff = diff.slice(0, AR_TABLE_PAGE_SIZE);
  const hiddenDiff = diff.length - shownDiff.length;
  const versionEntries: RecoveryChangeEntry[] = [
    ...shownDiff.map<RecoveryChangeEntry>((entry) => ({
      kind: 'value',
      key: entry.kind === 'field' ? `field:${entry.fieldId}` : `ref:${entry.entityOrGroupId}`,
      name: nameDiffEntry(entry),
      live: entry.live,
      retained: entry.retained,
    })),
    ...(hiddenDiff === 0
      ? []
      : [
          {
            kind: 'notice' as const,
            key: 'version:more',
            text: `${hiddenDiff} more ${hiddenDiff === 1 ? 'value is' : 'values are'} not listed here. Restoring covers all ${diff.length}.`,
          },
        ]),
    ...(grouped && diff.length === 0
      ? [{ kind: 'notice' as const, key: 'version:state', text: `Nothing changes on ${versionName}.` }]
      : []),
    ...(versionSavedAt === null
      ? []
      : [
          {
            kind: 'notice' as const,
            key: 'version:savedAt',
            text: `${versionName} was last saved at ${dayjs(versionSavedAt).format(MOMENT_FORMAT)}, which is the state these values come from.`,
          },
        ]),
  ];

  const groups: RecoveryChangeGroup[] = [
    { key: 'version', name: grouped ? versionName : null, entries: versionEntries },
    ...tables.map((table) => table.group),
  ];

  const valueSummary =
    diff.length === 0
      ? 'No values on the form itself change.'
      : `Restoring writes ${diff.length} ${diff.length === 1 ? 'value' : 'values'} back onto the live form.`;

  const lockedSummary =
    lockedCount === 0
      ? null
      : lockedCount === 1
        ? '1 row is locked, so restoring leaves it exactly as it is.'
        : `${lockedCount} rows are locked, so restoring leaves them exactly as they are.`;

  const stillReading = tableReads.some((read) => read.status === 'reading');
  const unreadableTable = tableReads.find((read) => read.status === 'refused');

  /**
   * The single gate, so every dead press carries its own words in the `Tooltip` —
   * the same shape `FormRecoveryRow` gives `Restore…`. Two of the reasons are the
   * tables: one still out means the diff is not yet the whole moment, and one that
   * could not be read at all has no digests to be restored by, so the moment can
   * only be restored in part. Confirming either is the choice this dialog is here to
   * make impossible.
   */
  const confirmDisabledReason =
    selectedCursorMicros === null || result === null
      ? 'Read this restore point before restoring.'
      : refusalMessage !== null
        ? 'Nothing was written. Close this and select the restore point again.'
        : stillReading
          ? 'A table of this moment is still being read.'
          : unreadableTable !== undefined
            ? `${tableNameOf(unreadableTable.tableId)} could not be read, so this moment cannot be restored. Close this and select it again.`
            : null;

  const confirmLabel =
    lockedCount === 0
      ? 'Restore'
      : lockedCount === 1
        ? 'Restore without the locked row'
        : `Restore without the ${lockedCount} locked rows`;

  const confirmAriaLabel =
    lockedCount === 0
      ? 'Confirm restoring this moment'
      : 'Confirm restoring this moment without the locked rows';

  const handleConfirm = async (): Promise<void> => {
    if (selectedCursorMicros === null || result === null) return;
    setRefusalMessage(null);
    try {
      await restore(selectedCursorMicros, result.liveLastModifiedMillis, {
        skipBlockedRows: lockedCount > 0,
      });
      messageApi.success('Restored this moment onto the form.');
      onClose();
      await refresh();
    } catch (error) {
      const { code, message } = readCallableError(error);
      if (code === 'failed-precondition') {
        const conflict = readRestoreConflict(error);
        setRefusalMessage(conflict ? describeConflict(conflict) : message || 'Nothing was written.');
        return;
      }
      handleFirestoreWriteError(error, '[FormRecoveryConfirmModalContent] restore', {
        userMessage: 'Could not restore this moment. Try again.',
      });
    }
  };

  return (
    <FlexCol gap={10} aria-label='Restore confirmation'>
      <FlexCol gap={0} aria-label='What this restore does'>
        <Text>{valueSummary}</Text>
        {tables.map((table) => (
          <Text key={table.group.key}>{table.summary}</Text>
        ))}
        {lockedSummary !== null && <Text>{lockedSummary}</Text>}
      </FlexCol>
      {momentAtMillis !== null && (
        <Text type='secondary'>
          {`From the restore point at ${dayjs(momentAtMillis).format(MOMENT_FORMAT)}.`}
        </Text>
      )}
      <FormRecoveryChangeTable groups={groups} />
      {refusalMessage !== null && (
        <Alert
          type='error'
          showIcon
          title='This restore was refused'
          description={refusalMessage}
          aria-label='Restore refused'
        />
      )}
      <FlexBox justifyEnd gap={5} noGrow stretch>
        <Button onClick={onClose} aria-label='Cancel restoring this moment'>
          Cancel
        </Button>
        <Tooltip title={confirmDisabledReason}>
          <span>
            <Button
              type='primary'
              loading={restoring}
              disabled={confirmDisabledReason !== null}
              onClick={() => void handleConfirm()}
              aria-label={confirmAriaLabel}
            >
              {confirmLabel}
            </Button>
          </span>
        </Tooltip>
      </FlexBox>
    </FlexCol>
  );
};

FormRecoveryConfirmModalContent.displayName = 'FormRecoveryConfirmModalContent';

export default FormRecoveryConfirmModalContent;
